import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetSubscriptionStatusQuery } from '@viome/viome-base/src/usecase/Misc/MiscQuery';
import { Button } from '@design-system/Button';
import { isEligibleForComponentization } from '@/utils/componentizationEligibility';
import { useIsComponentizationEnabled } from '@/hooks/useIsComponentizationEnabled';
import { useComponentizationEligibleSkus } from '@/hooks/useComponentizationEligibleSkus';
import { SHOP_PATH } from '@/constants/paths';
import { AccountManagementPageSubscriptionSkeleton } from '@screens/AccountManagementPage/AccountManagementPageSubscriptionSkeleton';
import { AccountSubscriptionCard } from '@screens/AccountManagementPage/AccountSubscriptionCard';
import { AccountComponentizedSubscriptionCard } from './AccountComponentizedSubscriptionCard';
import { EmptyBoxIcon } from '@/components/icons/EmptyBoxIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { LatestResultsBanner } from '@/components/shared/LatestResultsBanner';

export const AccountComponentizedSubscriptionSection = () => {
  const navigate = useNavigate();
  const isComponentizationEnabled = useIsComponentizationEnabled();
  const eligibleSkus = useComponentizationEligibleSkus();
  const { data, isLoading, isError, refetch } =
    useGetSubscriptionStatusQuery();
  const subscriptions = data?.subscriptions ?? [];

  useEffect(() => {
    refetch();
  }, [refetch]);

  if (isLoading) {
    return <AccountManagementPageSubscriptionSkeleton />;
  }

  if (isError || !data) {
    return null;
  }

  return (
    <div
      className="flex flex-col"
      data-testid="accountComponentized-subscriptions"
    >
      <LatestResultsBanner
        className="mb-6"
        message="Subscriptions based on latest test results."
      />
      
      {subscriptions.length > 0 && (
        <span
          className="typography-h2 text-primary mb-6"
          data-testid="accountComponentized-subscriptionsTitle"
        >
          My Subscriptions
        </span>
      )}

      {subscriptions.length === 0 ? (
        <div
          className="flex flex-col items-center gap-4 pt-4 -mb-8"
          data-testid="accountComponentized-emptyCard"
        >
          <EmptyBoxIcon className="text-[#A0A1AD]" />
          <p className="typography-body text-tertiary text-center">
            You currently don't have any active subscriptions.
          </p>
          <Button
            variant="secondary"
            className="w-full justify-start [&_svg]:!size-6"
            onClick={() => navigate(SHOP_PATH)}
            data-testid="accountComponentized-shopButton"
          >
            Shop your formulas
            <ChevronRightIcon
              width={24}
              height={24}
              aria-hidden
              className="text-tertiary ml-auto"
            />
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {subscriptions.map((subscription) =>
            isComponentizationEnabled &&
            isEligibleForComponentization(subscription, eligibleSkus) ? (
              <AccountComponentizedSubscriptionCard
                key={subscription.subscription_id}
                subscription={subscription}
              />
            ) : (
              <AccountSubscriptionCard
                key={subscription.subscription_id}
                subscription={subscription}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
};
